import React from 'react';
import { PrintableDocument, PrintStyles } from '../ui/PrintableDocument';
import { formatCurrency, formatDate } from '../../lib/utils';
import { ExternalLink } from 'lucide-react';

interface PrintableExpenseProps {
  expense: any;
  isVisible?: boolean;
}

export const PrintableExpense: React.FC<PrintableExpenseProps> = ({
  expense,
  isVisible = false
}) => {
  if (!expense) return null;

  const getCategoryColor = (category: string) => {
    const colors = [
      'bg-blue-100 text-blue-700 border-blue-200',
      'bg-green-100 text-green-700 border-green-200',
      'bg-purple-100 text-purple-700 border-purple-200',
      'bg-orange-100 text-orange-700 border-orange-200',
      'bg-pink-100 text-pink-700 border-pink-200',
      'bg-indigo-100 text-indigo-700 border-indigo-200',
    ];
    const index = category.length % colors.length;
    return colors[index];
  };

  return (
    <>
      <PrintStyles />
      <div className={`${isVisible ? 'block' : 'hidden'} print:block`}>
        <PrintableDocument type="expense" data={expense}>
          {/* Expense Details Section */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            {/* Expense Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                EXPENSE DETAILS
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Expense Date:</span>
                  <span className="font-medium">{formatDate(expense.date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Recorded:</span>
                  <span className="font-medium">{formatDate(expense.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Category:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getCategoryColor(expense.category)}`}>
                    {expense.category}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Vendor:</span>
                  <span className="font-medium">{expense.vendor}</span>
                </div>
              </div>
            </div>

            {/* Amount */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                AMOUNT
              </h3>
              <div className="bg-error-50 border border-error-200 rounded-lg p-6">
                <div className="text-center">
                  <p className="text-error-600 text-sm font-medium mb-2">Expense Amount</p>
                  <p className="text-4xl font-bold text-error-700">{formatCurrency(Number(expense.amount))}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Expense Summary Table */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">EXPENSE SUMMARY</h3>
            <table className="document-table w-full">
              <tbody>
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-700">Date:</td>
                  <td className="py-3 px-4">{formatDate(expense.date)}</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-700">Category:</td>
                  <td className="py-3 px-4">{expense.category}</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-700">Vendor:</td>
                  <td className="py-3 px-4">{expense.vendor}</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-700">Amount:</td>
                  <td className="py-3 px-4 font-bold text-error-600">{formatCurrency(Number(expense.amount))}</td>
                </tr>
                {expense.receipt_url && (
                  <tr>
                    <td className="py-3 px-4 font-medium text-gray-700">Receipt:</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <ExternalLink className="w-4 h-4 text-primary-600" />
                        <span className="text-primary-600">Receipt Available</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Notes */}
          {expense.notes && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">NOTES</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-700">{expense.notes}</p>
              </div>
            </div>
          )}

          {/* Receipt Information */}
          {expense.receipt_url && (
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Receipt Information</h4>
              <p className="text-blue-800 text-sm mb-2">
                A receipt or supporting document is available for this expense.
              </p>
              <p className="text-blue-600 text-sm font-mono break-all">{expense.receipt_url}</p>
            </div>
          )}

          {/* Expense Report Footer */}
          <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Expense Report</h4>
            <p className="text-gray-700 text-sm">
              This expense report is generated for business accounting purposes. 
              Please retain this document for tax and audit requirements.
            </p>
          </div>
        </PrintableDocument>
      </div>
    </>
  );
};